/**
 * Iter 265 — Hub API : paramétrage + surveillance unifiés de toutes les intégrations.
 * Iter 306 — Portée par société : chaque société peut surcharger les clés du tenant
 * (SMTP, Aircall, Yousign, Meta, GoCardless), avec repli automatique sur la config tenant.
 * Les modèles IA et les intégrations plateforme restent au niveau tenant/plateforme.
 */
import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useSociete } from "../contexts/SocieteContext";
import PageHeader from "../components/PageHeader";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import {
  Activity, CheckCircle2, AlertTriangle, XCircle, Plug, RefreshCw, Loader2,
  Server, Cloud, Mail, Phone, CreditCard, Megaphone, Sparkles, ShieldCheck,
  KeyRound, ChevronDown, ChevronUp, ExternalLink, Landmark,
  Calculator, FlaskConical, Building2, Undo2,
} from "lucide-react";

const ICONS = {
  mongodb: Server, emergent_llm: Sparkles, smtp: Mail,
  aircall: Phone, stripe: CreditCard, yousign: ShieldCheck, meta: Megaphone,
  intel_agent: Sparkles, onedrive: Cloud,
  cegid: Calculator, gocardless: Landmark,
  storecove: CreditCard, google_api: Cloud,
};

const STATUS_META = {
  ok: { label: "Opérationnel", color: "text-emerald-400", bg: "bg-emerald-500/10", border: "border-emerald-500/30", Icon: CheckCircle2 },
  warning: { label: "À vérifier", color: "text-amber-400", bg: "bg-amber-500/10", border: "border-amber-500/30", Icon: AlertTriangle },
  ko: { label: "Erreur", color: "text-rose-400", bg: "bg-rose-500/10", border: "border-rose-500/30", Icon: XCircle },
  not_configured: { label: "À configurer", color: "text-slate-400", bg: "bg-slate-500/10", border: "border-slate-500/30", Icon: Plug },
};

function ScopeBadge({ item, societeMode }) {
  if (!societeMode) return null;
  if (item.societe_overridable === false) {
    return (
      <span className="text-[9px] px-2 py-0.5 rounded-full font-bold bg-slate-500/10 text-slate-500" data-testid={`hub-scope-${item.id}`}>
        Niveau tenant / plateforme
      </span>
    );
  }
  const isSoc = item.config_scope === "societe";
  return (
    <span className={`text-[9px] px-2 py-0.5 rounded-full font-bold ${isSoc ? "bg-[#f7c935]/15 text-[#f7c935]" : "bg-sky-500/10 text-sky-400"}`}
      data-testid={`hub-scope-${item.id}`}>
      {isSoc ? "Propre à la société" : "Héritée du tenant"}
    </span>
  );
}

function IntegrationCard({ item, api, onRefresh, societeMode }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [resetting, setResetting] = useState(false);
  const st = STATUS_META[item.status] || STATUS_META.not_configured;
  const Icon = ICONS[item.id] || Plug;
  const hasConfig = item.config && (item.fields || []).length > 0;

  const save = async () => {
    setSaving(true);
    try {
      const payload = {};
      (item.fields || []).forEach(f => {
        if (form[f.key] !== undefined && form[f.key] !== "") payload[f.key] = form[f.key];
      });
      if (Object.keys(payload).length === 0) { toast.error("Renseignez au moins un champ"); setSaving(false); return; }
      if (item.config.method === "PUT") await api.put(item.config.url, payload);
      else await api.post(item.config.url, payload);
      toast.success(`${item.label} — configuration enregistrée${societeMode ? " pour la société" : ""}`);
      setForm({});
      setOpen(false);
      onRefresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Enregistrement impossible");
    }
    setSaving(false);
  };

  const runTest = async () => {
    setTesting(true);
    try {
      const r = item.test.method === "PUT" ? await api.put(item.test.url, {}) : await api.post(item.test.url, {});
      const ok = r.data?.ok !== false && r.data?.success !== false;
      if (ok) toast.success(`${item.label} — test réussi ✓`);
      else toast.error(`${item.label} — test en échec : ${r.data?.detail || r.data?.message || ""}`);
      onRefresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Test en échec");
    }
    setTesting(false);
  };

  const resetOverride = async () => {
    if (!window.confirm(`Supprimer la configuration propre à la société pour ${item.label} ?\nElle héritera de nouveau des clés du tenant.`)) return;
    setResetting(true);
    try {
      await api.delete(item.reset.url);
      toast.success(`${item.label} — retour à l'héritage tenant`);
      onRefresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Réinitialisation impossible");
    }
    setResetting(false);
  };

  return (
    <div className={`rounded-xl border p-4 transition-colors ${st.border} bg-[#14101c]`} data-testid={`hub-card-${item.id}`}>
      <div className="flex items-start gap-3">
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${st.bg}`}>
          <Icon className={`w-5 h-5 ${st.color}`} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-bold text-white">{item.label}</span>
            <span className={`text-[9px] px-2 py-0.5 rounded-full font-bold ${st.bg} ${st.color}`} data-testid={`hub-status-${item.id}`}>
              <st.Icon className="w-2.5 h-2.5 inline mr-0.5 -mt-0.5" />{st.label}
            </span>
            <ScopeBadge item={item} societeMode={societeMode} />
          </div>
          {item.description && <p className="text-[11px] text-slate-400 mt-1">{item.description}</p>}
          {item.details && <p className={`text-[10px] mt-1 ${st.color}`}>{item.details}</p>}
          {item.where_to_get && (
            <p className="text-[10px] text-slate-500 mt-1 flex items-start gap-1">
              <KeyRound className="w-3 h-3 mt-0.5 flex-shrink-0" /> {item.where_to_get}
            </p>
          )}
        </div>
        <div className="flex items-center gap-1.5 flex-shrink-0">
          {item.reset && (
            <button onClick={resetOverride} disabled={resetting} title="Revenir à l'héritage tenant"
              className="text-[10px] px-2 py-1.5 rounded-lg bg-rose-500/10 text-rose-400 hover:bg-rose-500/20 font-bold flex items-center gap-1"
              data-testid={`hub-reset-${item.id}`}>
              {resetting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Undo2 className="w-3 h-3" />} Hériter
            </button>
          )}
          {item.test && (
            <button onClick={runTest} disabled={testing} title="Tester la connexion"
              className="text-[10px] px-2 py-1.5 rounded-lg bg-white/5 text-slate-300 hover:bg-white/10 font-bold flex items-center gap-1"
              data-testid={`hub-test-${item.id}`}>
              {testing ? <Loader2 className="w-3 h-3 animate-spin" /> : <FlaskConical className="w-3 h-3" />} Tester
            </button>
          )}
          {item.link && !societeMode && (
            <button onClick={() => navigate(item.link.to)} title={item.link.label}
              className="text-[10px] px-2 py-1.5 rounded-lg bg-[#3b82f6]/10 text-[#3b82f6] hover:bg-[#3b82f6]/20 font-bold flex items-center gap-1"
              data-testid={`hub-link-${item.id}`}>
              <ExternalLink className="w-3 h-3" /> Ouvrir
            </button>
          )}
          {hasConfig && (
            <button onClick={() => setOpen(!open)}
              className="text-[10px] px-2 py-1.5 rounded-lg bg-[#f7c935]/10 text-[#f7c935] hover:bg-[#f7c935]/20 font-bold flex items-center gap-1"
              data-testid={`hub-configure-${item.id}`}>
              <KeyRound className="w-3 h-3" /> Configurer {open ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            </button>
          )}
        </div>
      </div>

      {open && hasConfig && (
        <div className="mt-3 pt-3 border-t border-white/5 grid sm:grid-cols-2 gap-2" data-testid={`hub-config-form-${item.id}`}>
          {societeMode && (
            <p className="sm:col-span-2 text-[10px] text-[#f7c935]/80">
              Ces clés seront propres à cette société. Laissez un champ vide pour conserver l'héritage du tenant.
            </p>
          )}
          {(item.fields || []).map(f => (
            f.type === "checkbox" ? (
              <label key={f.key} className="flex items-center gap-2 text-xs text-slate-300 cursor-pointer sm:col-span-2">
                <input type="checkbox" checked={!!form[f.key]} onChange={e => setForm({ ...form, [f.key]: e.target.checked })}
                  className="accent-[#f7c935]" data-testid={`hub-field-${item.id}-${f.key}`} />
                {f.label}
              </label>
            ) : (
              <div key={f.key}>
                <label className="text-[10px] text-slate-500 mb-1 block">{f.label}</label>
                <input type={f.type === "password" ? "password" : "text"} value={form[f.key] || ""}
                  onChange={e => setForm({ ...form, [f.key]: e.target.value })}
                  placeholder={f.placeholder || (item.configured ? "•••••• (déjà configuré — laisser vide pour conserver)" : "")}
                  className="w-full bg-[#0b0811] border border-white/10 rounded-lg px-3 py-2 text-xs text-white focus:border-[#f7c935]/50 outline-none"
                  data-testid={`hub-field-${item.id}-${f.key}`} />
              </div>
            )
          ))}
          <div className="sm:col-span-2 flex justify-end gap-2 mt-1">
            <Button size="sm" variant="outline" onClick={() => { setOpen(false); setForm({}); }} className="border-white/10 text-slate-400 h-8" data-testid={`hub-cancel-${item.id}`}>Annuler</Button>
            <Button size="sm" onClick={save} disabled={saving} className="bg-[#f7c935] text-[#0b0811] hover:bg-[#e6b825] font-bold h-8" data-testid={`hub-save-${item.id}`}>
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5 mr-1" />} Enregistrer
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function IntegrationsHealthPage() {
  const { axiosAuth } = useAuth();
  const { societes } = useSociete() || {};
  const api = axiosAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [societeId, setSocieteId] = useState(() => localStorage.getItem("blitzz_hub_societe_id") || "");

  const selectSociete = (sid) => {
    setSocieteId(sid);
    try { localStorage.setItem("blitzz_hub_societe_id", sid); } catch {}
  };

  const load = useCallback(async (silent = false, sid = societeId) => {
    if (!silent) setLoading(true);
    else setRefreshing(true);
    try {
      const r = await api.get(`/integrations/hub${sid ? `?societe_id=${sid}` : ""}`);
      setData(r.data);
    } catch {
      toast.error("Impossible de charger le Hub API");
    }
    setLoading(false);
    setRefreshing(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [societeId]);

  useEffect(() => { load(); }, [load]);

  // Sélection mémorisée invalide (société supprimée / autre compte) → retour au mode tenant
  useEffect(() => {
    if (societeId && (societes || []).length > 0 && !societes.find(s => s.societe_id === societeId)) {
      selectSociete("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [societes, societeId]);

  const societeMode = !!societeId;
  const selectedSociete = (societes || []).find(s => s.societe_id === societeId);

  return (
    <div className="p-6 max-w-[1200px] mx-auto" data-testid="api-hub-page">
      <PageHeader
        icon={<Activity />}
        title="Hub API"
        description="Paramétrage et surveillance de toutes les intégrations, au même endroit"
        testId="api-hub-header"
        actions={(
          <Button onClick={() => load(true)} disabled={refreshing} className="bg-[#f7c935] text-[#0b0811] hover:bg-[#e6b825] font-bold" data-testid="hub-refresh-btn">
            {refreshing ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1" />} Tout re-vérifier
          </Button>
        )}
      />

      {/* Portée : tenant ou société */}
      {(societes || []).length > 0 && (
        <div className="mb-5 bg-[#14101c] border border-white/5 rounded-xl p-4 flex flex-wrap items-center gap-3" data-testid="hub-scope-selector">
          <div className="flex items-center gap-2 text-xs font-bold text-slate-400">
            <Building2 className="w-4 h-4 text-[#f7c935]" /> Portée de la configuration :
          </div>
          <select
            value={societeId}
            onChange={e => selectSociete(e.target.value)}
            className="bg-[#0b0811] border border-white/10 rounded-lg px-3 py-2 text-xs text-white focus:border-[#f7c935]/50 outline-none min-w-[240px]"
            data-testid="hub-societe-select"
          >
            <option value="">Configuration du tenant (par défaut)</option>
            {(societes || []).map(s => (
              <option key={s.societe_id} value={s.societe_id}>Société — {s.name || s.nom}</option>
            ))}
          </select>
          {societeMode && (
            <p className="text-[10px] text-slate-500 flex-1 min-w-[200px]" data-testid="hub-societe-note">
              Les clés propres à <b className="text-[#f7c935]">{selectedSociete?.name || selectedSociete?.nom || "cette société"}</b> priment sur celles du tenant.
              Sans surcharge, la société hérite automatiquement de la configuration du tenant.
              Les modèles IA restent gérés au niveau tenant/plateforme.
            </p>
          )}
        </div>
      )}

      {loading ? (
        <div className="text-center py-16 text-slate-500"><Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" /> Vérification des intégrations...</div>
      ) : !data ? (
        <div className="text-center py-16 text-rose-400">Erreur de chargement</div>
      ) : (
        <>
          {/* Summary */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="hub-summary">
            {[
              { key: "ok", label: "Opérationnelles", value: data.summary.ok, color: "#22c55e" },
              { key: "warning", label: "À vérifier", value: data.summary.warning, color: "#f59e0b" },
              { key: "ko", label: "En erreur", value: data.summary.ko, color: "#ef4444" },
              { key: "not_configured", label: "À configurer", value: data.summary.not_configured, color: "#64748b" },
            ].map(s => (
              <div key={s.key} className="bg-[#14101c] border border-white/5 rounded-xl p-4" data-testid={`hub-summary-${s.key}`}>
                <div className="text-[10px] uppercase font-bold tracking-wide text-slate-500 mb-1">{s.label}</div>
                <div className="text-2xl font-black" style={{ color: s.color }}>{s.value}</div>
              </div>
            ))}
          </div>

          {/* Categories */}
          <div className="space-y-6">
            {data.categories.map(cat => (
              <div key={cat.category} data-testid={`hub-category-${cat.category.replace(/[^a-z]/gi, "")}`}>
                <h2 className="text-xs uppercase tracking-wider font-black text-slate-500 mb-2 flex items-center gap-2">
                  {cat.category}
                  <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-white/5 font-bold">{cat.items.length}</span>
                </h2>
                <div className="space-y-2">
                  {cat.items.map(item => (
                    <IntegrationCard key={item.id} item={item} api={api} onRefresh={() => load(true)} societeMode={societeMode} />
                  ))}
                </div>
              </div>
            ))}
          </div>

          <p className="text-[10px] text-slate-600 mt-6">
            Vérifié le {(data.checked_at || "").slice(0, 16).replace("T", " ")} · Les clés saisies sont chiffrées en base et jamais réaffichées en clair. Le PDF récapitulatif "Clés API" est disponible dans le Centre d'aide.
          </p>
        </>
      )}
    </div>
  );
}
